import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useCreateKnowledgeBase, useUpdateKnowledgeBase } from '../queries';
import type { KnowledgeBase } from '../types';

/**
 * Create/edit a knowledge base. `base` = edit mode; null = create. The parent
 * remounts this via a `key` when the target changes, so state initializes from
 * props directly (no reset effect — mirrors CollectionDialog).
 *
 * Manual creation is always **Organization-scoped**: project and collection KBs
 * are system-managed (auto-created on first sync, one per project/collection), so
 * hand-creating them here would clash with that unique corpus. Editing only
 * touches name/description — an auto-created KB keeps its scope.
 */
export function KnowledgeBaseDialog({
  base,
  open,
  onOpenChange,
  onCreated,
}: {
  base: KnowledgeBase | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onCreated?: (base: KnowledgeBase) => void;
}) {
  const create = useCreateKnowledgeBase();
  const update = useUpdateKnowledgeBase();

  const [name, setName] = useState(base?.name ?? '');
  const [description, setDescription] = useState(base?.description ?? '');

  const submit = () => {
    if (base) {
      // Edit: never change scope/project — only the editable metadata.
      update.mutate(
        { id: base.public_id, input: { name: name.trim(), description: description.trim() } },
        { onSuccess: () => onOpenChange(false) },
      );
    } else {
      create.mutate(
        { name: name.trim(), description: description.trim(), scope: 'org', is_active: true },
        {
          onSuccess: (created) => {
            onOpenChange(false);
            onCreated?.(created);
          },
        },
      );
    }
  };

  const pending = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{base ? 'Edit knowledge base' : 'New knowledge base'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="kb-name">Name</Label>
            <Input
              id="kb-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Product docs"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="kb-description">Description</Label>
            <Textarea
              id="kb-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What this base is for"
              rows={3}
            />
          </div>

          {base ? null : (
            <p className="text-muted-foreground text-xs">
              Organization-wide corpus. Project and collection knowledge bases are created
              automatically when you sync a project or wiki collection.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={pending || !name.trim()}>
            {base ? 'Save' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
